import { v1 as uuidv1 } from 'uuid';

import { createConfigParametersRepository } from '../../configParametersRepository';
import { EVENTS_STORAGE_KEY, STORAGE_KEY } from '../resources/constants';
import type { GtagEvent, StoredEvents } from './types';

export const RECOVERED = 'recovered';

const getStorageClient = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  return getTrackingStorageClient();
};

export const getStoredEvents = (): StoredEvents => {
  return (getStorageClient()?.getFromStorage('sessionStorage', STORAGE_KEY)?.[
    EVENTS_STORAGE_KEY
  ] || {}) as StoredEvents;
};

const setStoredEvents = (events: StoredEvents = {}) => {
  const storageClient = getStorageClient();
  storageClient?.update(EVENTS_STORAGE_KEY, events);
};

/**
 * Push an event to storage, if the storage is full, remove the oldest event
 * @param gtagEvent - ga4 event
 * @param maxStored - max number of events to store, default is 10
 * @returns uuid of the queued event
 */
export const pushStoredEvent = (
  gtagEvent: GtagEvent,
  maxStored = 10
): string | undefined => {
  if (gtagEvent[2].misc === RECOVERED) {
    // in order to avoid infinite loops avoid queueing events being recovered
    return;
  }
  let storedEvents = getStoredEvents();
  const keys = Object.keys(storedEvents);
  // keys are already sorted because uuidv1 creates sequenced ids
  if (keys.length >= maxStored && maxStored > 0) {
    storedEvents = removeStoredEvent(keys[0]);
  }
  const uuid = uuidv1();
  storedEvents[uuid] = gtagEvent;
  setStoredEvents(storedEvents);
  return uuid;
};

/**
 * Remove an event from storage, returns the new storage
 * @param uuid - uuid of the event to remove
 * @returns new storage without the removed event
 */
export const removeStoredEvent = (uuid: string): StoredEvents => {
  const storedEvents = getStoredEvents();
  if (!storedEvents[uuid]) {
    return storedEvents;
  }
  const { [uuid]: _delete, ...newStoredEvents } = storedEvents;
  setStoredEvents(newStoredEvents);
  return newStoredEvents;
};
